import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
} from "@mui/material";
import InfoHeader from "../components/InfoHeader";
import { useEffect, useState, useRef } from "react";
import Empresa from "../types/negocio/Empresa";
import { empresaService } from "../services/EmpresaService";
import toastService from "../utils/toastService";
import theme from "../themes/theme";
import MetaGlobal from "../types/negocio/MetaGlobal";
import { metaGlobalService } from "../services/MetaGlobalService";
import CardMetasGlobais from "../components/CardMetasGlobais";
import { swotService } from "../services/SwotService";
import Swot from "../types/negocio/Swot";
import { styled } from "@mui/material/styles";
import PlanoAcao from "../types/negocio/PlanoAcao";
import PlanoAcaoResultadoConsulta from "../types/negocio/PlanoAcaoResultadoConsulta";
import { planoAcaoService } from "../services/PlanoAcaoService";
import { EmptyMessage } from "../components/EmptyMessage";
import { PrimaryButton } from "../components";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        fontWeight: 600,
        paddingTop: 12,
        paddingBottom: 14,
        whiteSpace: "normal",
        wordWrap: "break-word",
        textAlign: "center",
        border: '1px solid #e0e0e0 !important',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        paddingTop: 8,
        paddingBottom: 8,
        textAlign: "center",
        border: '1px solid #e0e0e0 !important',
        backgroundColor: '#ffffff',
    },
}));


const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

export default function MapaEstrategico() {
    const [empresa, setEmpresa] = useState<Empresa>({} as Empresa);
    const [isLoadingMetas, setIsLoadingMetas] = useState(true);
    const [isLoadingAcoes, setIsLoadingAcoes] = useState(true);
    const [isLoadingSwot, setIsLoadingSwot] = useState(true);
    const [isExportingPDF, setIsExportingPDF] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [metas, setMetas] = useState<MetaGlobal[]>([]);
    const [pontosFortes, setPontosFortes] = useState<Swot[]>([]);
    const [oportunidades, setOportunidades] = useState<Swot[]>([]);
    const [pontosFracos, setPontosFracos] = useState<Swot[]>([]);
    const [ameacas, setAmeacas] = useState<Swot[]>([]);
    const [acoes, setAcoes] = useState<PlanoAcao[]>([]);
    const [acoesDetalhadas, setAcoesDetalhadas] = useState<PlanoAcaoResultadoConsulta[]>([]);
    const contentRef = useRef<HTMLDivElement>(null);

    const maxRowsSwot = Math.max(
        pontosFortes.length,
        pontosFracos.length,
        oportunidades.length,
        ameacas.length
    );

    const maxRowsAcoes = Math.max(
        acoes.filter((a) => a.area === "Vendas").length,
        acoes.filter((a) => a.area === "Pessoas").length,
        acoes.filter((a) => a.area === "Finanças").length,
        acoes.filter((a) => a.area === "Processos / Produção").length
    );

    useEffect(() => {
        const fetchAcoes = async () => {
            try {
                setIsLoadingAcoes(true);
                const acoesData = await planoAcaoService.listarTodos();
                setAcoes(acoesData);
                
                // Buscar dados detalhados para o PDF
                const acoesDetalhadasData = await planoAcaoService.buscarPlanos({ verArquivados: false });
                setAcoesDetalhadas(acoesDetalhadasData);
            } catch {
                toastService.error("Erro ao carregar ações");
            } finally {
                setIsLoadingAcoes(false);
            }
        };

        fetchAcoes();
    }, []);

    useEffect(() => {
        const fetchMetas = async () => {
            try {
                setIsLoadingMetas(true);
                const fetchedMetas = await metaGlobalService.listarTodos();
                setMetas(fetchedMetas);
            } catch {
                toastService.error("Erro ao carregar metas globais");
            } finally {
                setIsLoadingMetas(false);
            }
        };

        fetchMetas();
    }, []);

    useEffect(() => {
        const fetchEmpresa = async () => {
            try {
                setError(null);
                const data = await empresaService.getEmpresa();
                if (data) {
                    setEmpresa(data);
                }
            } catch (err) {
                console.error("Erro ao carregar empresa:", err);
                setError("Não foi possível carregar os dados da empresa. Por favor, tente novamente.");
                toastService.error("Erro ao carregar dados da empresa");
            }
        };

        fetchEmpresa();
    }, []);

    const areasFixas = ["Vendas", "Finanças", "Processos / Produção", "Pessoas"];
    const metasPorArea = metas.reduce<Record<string, MetaGlobal[]>>((acc, meta) => {
        const area = meta.area;
        if (!acc[area]) {
            acc[area] = [];
        }
        acc[area].push(meta);
        return acc;
    }, {});

    useEffect(() => {
        setIsLoadingSwot(true);
        swotService.obterQuadrante().then((data) => {
            setPontosFortes(data.pontosFortes);
            setPontosFracos(data.pontosFracos);
            setOportunidades(data.oportunidades);
            setAmeacas(data.ameacas);
            setIsLoadingSwot(false);
        }).catch((error) => {
            console.error("Erro ao carregar os dados do SWOT:", error);
            toastService.error("Erro ao carregar os dados do SWOT");
            setIsLoadingSwot(false);
        });
    }, []);

    const getByPontuacao = (items: Swot[]) => {
        return [...items]
            .sort((a, b) => (b.pontuacao || 0) - (a.pontuacao || 0));
    };

    const exportToPDF = async () => {
        if (!contentRef.current) {
            toastService.error("Erro ao gerar PDF");
            return;
        }

        if (acoesDetalhadas.length === 0 && !isLoadingAcoes) {
            toastService.error("Nenhum plano de ação encontrado para exportar");
            return;
        }

        try {
            setIsExportingPDF(true);

            // Configurações para capturar a tela
            const canvas = await html2canvas(contentRef.current, {
                useCORS: true,
                allowTaint: true,
                background: '#ffffff',
                logging: false,
                width: window.innerWidth,
                scale: 1,
                letterRendering: true,
                // @ts-ignore - onclone existe na versão atual do html2canvas mas os tipos podem estar desatualizados
                onclone: (clonedDoc: Document, element: HTMLElement) => {
                    // Aplicar estilos para ocupar 100vw no elemento raiz
                    element.style.width = '100vw';
                    element.style.maxWidth = '100vw';
                    element.style.margin = '0';
                    element.style.padding = '40px';
                    element.style.boxSizing = 'border-box';

                    const allTextElements = clonedDoc.querySelectorAll('h1, h2, h3, h4, h5, h6, td, th, li, a');
                    allTextElements.forEach(textElement => {
                        const htmlElement = textElement as HTMLElement;
                        htmlElement.style.paddingBottom = '20px';
                        htmlElement.style.verticalAlign = 'middle';
                        htmlElement.style.lineHeight = '1.4';
                        
                        // Aumentar o tamanho da fonte para o PDF
                        const currentFontSize = window.getComputedStyle(htmlElement).fontSize;
                        const currentSizeValue = parseFloat(currentFontSize);
                        const newSizeValue = currentSizeValue * 1.25; // Aumenta 25% tamanho da fonte
                        htmlElement.style.fontSize = `${newSizeValue}px`;
                    });

                    const littleTextElements = clonedDoc.querySelectorAll('p, span, div');
                    littleTextElements.forEach(textElement => {
                        const htmlElement = textElement as HTMLElement;

                        // Aumentar o tamanho da fonte para o PDF
                        const currentFontSize = window.getComputedStyle(htmlElement).fontSize;
                        const currentSizeValue = parseFloat(currentFontSize);
                        const newSizeValue = currentSizeValue * 1.25; // Aumenta 25% tamanho da fonte
                        htmlElement.style.fontSize = `${newSizeValue}px`;
                    });

                    // Criar header personalizado para o PDF
                    const header = clonedDoc.createElement('div');
                    header.style.cssText = `
                        height: 100px;
                        width: calc(100vw + 40px);
                        background: linear-gradient(135deg, #042E4B 0%, #042E4B 65%, #EF443C 65%, #EF443C 100%);
                        margin: -40px -40px 40px -40px;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        padding: 0 60px;
                        overflow: hidden;
                        box-sizing: border-box;
                        position: relative;
                    `;

                    // Criar container para logo
                    const logoContainer = clonedDoc.createElement('div');
                    logoContainer.style.cssText = `
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 15px;
                        margin-bottom: 16px;
                    `;

                    // Criar elemento da logo
                    const logo = clonedDoc.createElement('img');
                    logo.src = '/logo512teste.png';
                    logo.style.cssText = `
                        width: 60px;
                        height: 40px;
                        box-sizing: border-box;
                        margin-top: 18px;
                    `;

                    // Criar texto da marca
                    const brandText = clonedDoc.createElement('div');
                    brandText.style.cssText = `
                        color: white;
                        font-size: 24px;
                        font-weight: semibold;
                        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
                    `;
                    brandText.textContent = 'ThinkWay';

                    // Montar container da logo
                    logoContainer.appendChild(logo);
                    logoContainer.appendChild(brandText);

                    // Criar elementos decorativos
                    const decorativeElement1 = clonedDoc.createElement('div');
                    decorativeElement1.style.cssText = `
                        position: absolute;
                        top: -10px;
                        right: 30%;
                        width: 80px;
                        height: 80px;
                        border-radius: 50%;
                        background: rgba(239, 68, 60, 0.1);
                        z-index: 1;
                    `;

                    const decorativeElement2 = clonedDoc.createElement('div');
                    decorativeElement2.style.cssText = `
                        position: absolute;
                        bottom: -15px;
                        right: 15%;
                        width: 60px;
                        height: 60px;
                        border-radius: 50%;
                        background: rgba(255, 255, 255, 0.1);
                        z-index: 1;
                    `;

                    // Montar o header completo
                    header.appendChild(decorativeElement1);
                    header.appendChild(decorativeElement2);
                    header.appendChild(logoContainer);

                    // Inserir o header no início do documento
                    const firstChild = element.firstChild;
                    if (firstChild) {
                        element.insertBefore(header, firstChild);
                    } else {
                        element.appendChild(header);
                    }
                }
            } as any);

            const imgData = canvas.toDataURL('image/png');

            // Criar PDF com margens
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const margin = 0; // Sem margens laterais para ocupar 100% da largura
            const topMargin = 0; // topo absoluto
            const bottomMargin = 10;

            const availableWidth = pdfWidth - (margin * 2);
            const availableHeight = pdfHeight - topMargin - bottomMargin;

            const imgWidth = canvas.width;
            const imgHeight = canvas.height;

            // Calcular proporções com margens
            const ratio = Math.min(availableWidth / imgWidth, availableHeight / imgHeight);
            const scaledWidth = imgWidth * ratio;
            const scaledHeight = imgHeight * ratio;

            // Centralizar na página considerando as margens
            const x = margin + (availableWidth - scaledWidth) / 2;
            const y = topMargin;

            // Se a imagem for muito alta, dividir em páginas
            if (scaledHeight > availableHeight) {
                let currentY = 0;
                const pageContentHeight = availableHeight;
                const originalImageHeight = scaledHeight;

                while (currentY < originalImageHeight) {
                    // Calcular quanto da imagem cabe nesta página
                    const remainingHeight = originalImageHeight - currentY;
                    const currentPageHeight = Math.min(pageContentHeight, remainingHeight);

                    // Calcular qual parte da imagem original mostrar
                    const sourceY = (currentY / scaledHeight) * imgHeight;
                    const sourceHeight = (currentPageHeight / scaledHeight) * imgHeight;

                    // Criar um canvas temporário com apenas a parte que queremos
                    const tempCanvas = document.createElement('canvas');
                    const tempCtx = tempCanvas.getContext('2d');
                    tempCanvas.width = imgWidth;
                    tempCanvas.height = sourceHeight;

                    if (tempCtx) {
                        tempCtx.drawImage(
                            canvas,
                            0, sourceY, imgWidth, sourceHeight,  // Fonte
                            0, 0, imgWidth, sourceHeight        // Destino
                        );

                        const pageImgData = tempCanvas.toDataURL('image/png');
                        pdf.addImage(pageImgData, 'PNG', x, y, scaledWidth, currentPageHeight);
                    }

                    currentY += currentPageHeight;

                    if (currentY < originalImageHeight) {
                        pdf.addPage();
                    }
                }
            } else {
                pdf.addImage(imgData, 'PNG', x, y, scaledWidth, scaledHeight);
            }

            // Adicionar segunda página com tabela de plano de ação
            pdf.addPage();
            
            // Capturar a tabela oculta de plano de ação
            const hiddenTable = document.getElementById('hidden-plano-acao-table');
            if (hiddenTable) {
                const tableCanvas = await html2canvas(hiddenTable, {
                    useCORS: true,
                    allowTaint: true,
                    background: '#ffffff',
                    logging: false,
                    letterRendering: true,
                    // @ts-ignore - onclone existe na versão atual do html2canvas mas os tipos podem estar desatualizados
                    onclone: (clonedDoc: Document, element: HTMLElement) => {
                        // Aplicar os mesmos ajustes de fonte das outras tabelas, mas com multiplicador menor
                        const allTextElements = clonedDoc.querySelectorAll('h1, h2, h3, h4, h5, h6, td, th, li, a');
                        allTextElements.forEach(textElement => {
                            const htmlElement = textElement as HTMLElement;
                            htmlElement.style.paddingBottom = '24px';
                            htmlElement.style.verticalAlign = 'middle';
                            htmlElement.style.lineHeight = '1.4';
                            
                            // Aumentar o tamanho da fonte, mas menos que as outras para manter consistência
                            const currentFontSize = window.getComputedStyle(htmlElement).fontSize;
                            const currentSizeValue = parseFloat(currentFontSize);
                            const newSizeValue = currentSizeValue * 1.1; // Aumenta apenas 10% em vez de 25%
                            htmlElement.style.fontSize = `${newSizeValue}px`;
                        });

                        const littleTextElements = clonedDoc.querySelectorAll('p, span, div');
                        littleTextElements.forEach(textElement => {
                            const htmlElement = textElement as HTMLElement;

                            // Aumentar o tamanho da fonte, mas menos que as outras para manter consistência
                            const currentFontSize = window.getComputedStyle(htmlElement).fontSize;
                            const currentSizeValue = parseFloat(currentFontSize);
                            const newSizeValue = currentSizeValue;
                            htmlElement.style.fontSize = `${newSizeValue}px`;
                        });
                    }
                } as any);

                const tableImgData = tableCanvas.toDataURL('image/png');
                const tableImgWidth = tableCanvas.width;
                const tableImgHeight = tableCanvas.height;

                // Configurar páginas da tabela
                const pageWidth = pdf.internal.pageSize.getWidth();
                const pageHeight = pdf.internal.pageSize.getHeight();
                const marginPage2 = 5; // Margem lateral reduzida para 5mm
                const topMargin = 10; // Margem superior
                const bottomMargin = 10; // Margem inferior

                const availableWidth = pageWidth - (marginPage2 * 2);
                const availableHeight = pageHeight - topMargin - bottomMargin;

                // Calcular proporções mantendo a qualidade
                const ratio = Math.min(availableWidth / tableImgWidth, availableHeight / tableImgHeight);
                const scaledWidth = tableImgWidth * ratio;
                const scaledHeight = tableImgHeight * ratio;

                // Centralizar na página
                const x = marginPage2 + (availableWidth - scaledWidth) / 2;
                const y = topMargin;

                // Se a imagem da tabela for muito alta, dividir em páginas
                if (scaledHeight > availableHeight) {
                    let currentY = 0;
                    const pageContentHeight = availableHeight;
                    const originalImageHeight = scaledHeight;

                    while (currentY < originalImageHeight) {
                        // Calcular quanto da imagem cabe nesta página
                        const remainingHeight = originalImageHeight - currentY;
                        const currentPageHeight = Math.min(pageContentHeight, remainingHeight);

                        // Calcular qual parte da imagem original mostrar
                        const sourceY = (currentY / scaledHeight) * tableImgHeight;
                        const sourceHeight = (currentPageHeight / scaledHeight) * tableImgHeight;

                        // Criar um canvas temporário com apenas a parte que queremos
                        const tempCanvas = document.createElement('canvas');
                        const tempCtx = tempCanvas.getContext('2d');
                        tempCanvas.width = tableImgWidth;
                        tempCanvas.height = sourceHeight;

                        if (tempCtx) {
                            tempCtx.drawImage(
                                tableCanvas,
                                0, sourceY, tableImgWidth, sourceHeight,  // Fonte
                                0, 0, tableImgWidth, sourceHeight        // Destino
                            );

                            const pageImgData = tempCanvas.toDataURL('image/png');
                            pdf.addImage(pageImgData, 'PNG', x, y, scaledWidth, currentPageHeight);
                        }

                        currentY += currentPageHeight;

                        if (currentY < originalImageHeight) {
                            pdf.addPage();
                        }
                    }
                } else {
                    pdf.addImage(tableImgData, 'PNG', x, y, scaledWidth, scaledHeight);
                }
            }

            // Nome do arquivo com data e hora
            const now = new Date();
            const fileName = `Mapa_Estrategico_${empresa.nome || 'Empresa'}_${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}.pdf`;

            pdf.save(fileName);
            toastService.success("PDF exportado com sucesso!");

        } catch (error) {
            console.error("Erro ao exportar PDF:", error);
            toastService.error("Erro ao gerar PDF. Tente novamente.");
        } finally {
            setIsExportingPDF(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} ref={contentRef}>

                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        alignItems: "stretch"
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <Paper
                            elevation={2}
                            sx={{
                                borderRadius: 2,
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                                flex: 1,
                                backgroundColor: '#ffffff !important',
                                border: '1px solid #e0e0e0',
                                boxShadow: 'none !important',
                            }}
                            style={{
                                backgroundColor: '#ffffff',
                                border: '1px solid #e0e0e0'
                            }}
                        >
                            <Box
                                sx={{
                                    py: 3,
                                    textAlign: "center",
                                    backgroundColor: theme.palette.secondary.main,
                                }}
                            >
                                <Typography variant="h5" sx={{ fontWeight: "medium", color: "white" }}>
                                    {empresa.nome}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    p: 3,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    backgroundColor: "white",
                                    flex: 1,
                                }}
                            >
                                <Typography fontSize={18} sx={{ color: "primary.main" }}>
                                    Metas Globais
                                </Typography>

                                {isLoadingMetas ? (
                                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                        <CircularProgress size={32} />
                                    </Box>
                                ) : (
                                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                                        {areasFixas.map((area) => (
                                            <CardMetasGlobais
                                                key={area}
                                                area={area}
                                                metas={metasPorArea[area] || []}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 2 }}>
                        <Paper
                            elevation={2}
                            sx={{
                                p: 3,
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                backgroundColor: '#ffffff !important',
                                border: '1px solid #e0e0e0',
                                boxShadow: 'none !important',
                            }}
                            style={{
                                backgroundColor: '#ffffff',
                                border: '1px solid #e0e0e0'
                            }}
                        >
                            <Typography fontSize={14} sx={{ color: "#607C90" }}>
                                Missão
                            </Typography>
                            <Typography fontSize={16}>
                                {empresa.missao}
                            </Typography>
                        </Paper>
                        <Paper
                            elevation={2}
                            sx={{
                                p: 3,
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                backgroundColor: '#ffffff !important',
                                border: '1px solid #e0e0e0',
                                boxShadow: 'none !important',
                            }}
                            style={{
                                backgroundColor: '#ffffff',
                                border: '1px solid #e0e0e0'
                            }}
                        >
                            <Typography fontSize={14} sx={{ color: "#607C90" }}>
                                Visão
                            </Typography>
                            <Typography fontSize={16}>
                                {empresa.visao}
                            </Typography>
                        </Paper>
                        <Paper
                            elevation={2}
                            sx={{
                                p: 3,
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                backgroundColor: '#ffffff !important',
                                border: '1px solid #e0e0e0',
                                boxShadow: 'none !important',
                            }}
                            style={{
                                backgroundColor: '#ffffff',
                                border: '1px solid #e0e0e0'
                            }}
                        >
                            <Typography fontSize={14} sx={{ color: "#607C90" }}>
                                Valores
                            </Typography>
                            <Typography fontSize={16}>
                                {empresa.valores}
                            </Typography>
                        </Paper>
                    </Box>
                </Box>

                <Paper
                    elevation={2}
                    sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#ffffff !important',
                        border: '1px solid #e0e0e0',
                        boxShadow: 'none !important',
                    }}
                    style={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e0e0e0'
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: (theme) => theme.palette.primary.main,
                            color: 'white',
                            py: 2,
                            textAlign: 'center',
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                        }}
                    >
                        <Typography fontSize={20} fontWeight="semibold" component="h1">
                            Análise SWOT
                        </Typography>
                    </Box>

                    <TableContainer>
                        <Table
                            sx={{
                                tableLayout: "fixed",
                                width: "100%",
                                border: '1px solid #e0e0e0 !important',
                                '& td, & th': {
                                    border: '1px solid #e0e0e0 !important',
                                }
                            }}
                            style={{
                                border: '1px solid #e0e0e0',
                                borderCollapse: 'collapse'
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>Pontos Fortes</StyledTableCell>
                                    <StyledTableCell>Pontos Fracos</StyledTableCell>
                                    <StyledTableCell>Oportunidades</StyledTableCell>
                                    <StyledTableCell>Ameaças</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoadingSwot ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                            <CircularProgress size={32} />
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    Array.from({ length: maxRowsSwot }).map((_, index) => (
                                        <StyledTableRow key={index}>
                                            <StyledTableCell>
                                                {getByPontuacao(pontosFortes)[index]?.descricao || (
                                                    <EmptyMessage> </EmptyMessage>
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {getByPontuacao(pontosFracos)[index]?.descricao || (
                                                    <EmptyMessage> </EmptyMessage>
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {getByPontuacao(oportunidades)[index]?.descricao || (
                                                    <EmptyMessage> </EmptyMessage>
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {getByPontuacao(ameacas)[index]?.descricao || (
                                                    <EmptyMessage> </EmptyMessage>
                                                )}
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Paper
                    elevation={2}
                    sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#ffffff !important',
                        border: '1px solid #e0e0e0',
                        boxShadow: 'none !important',
                    }}
                    style={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e0e0e0'
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: (theme) => theme.palette.primary.main,
                            color: 'white',
                            py: 2,
                            textAlign: 'center',
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                        }}
                    >
                        <Typography fontSize={20} fontWeight="semibold" component="h1">
                            Ações Estratégicas
                        </Typography>
                    </Box>

                    <TableContainer>
                        <Table
                            sx={{
                                tableLayout: "fixed",
                                width: "100%",
                                border: '1px solid #e0e0e0 !important',
                                '& td, & th': {
                                    border: '1px solid #e0e0e0 !important',
                                }
                            }}
                            style={{
                                border: '1px solid #e0e0e0',
                                borderCollapse: 'collapse'
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>Vendas</StyledTableCell>
                                    <StyledTableCell>Pessoas</StyledTableCell>
                                    <StyledTableCell>Finanças</StyledTableCell>
                                    <StyledTableCell>Processos / Produção</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoadingAcoes ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                            <CircularProgress size={32} />
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    Array.from({ length: maxRowsAcoes }).map((_, index) => (
                                        <StyledTableRow key={index}>
                                            <StyledTableCell>
                                                {acoes.filter((a) => a.area === "Vendas")[index]?.acao || (
                                                    <EmptyMessage> </EmptyMessage>
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {acoes.filter((a) => a.area === "Pessoas")[index]?.acao || (
                                                    <EmptyMessage> </EmptyMessage>
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {acoes.filter((a) => a.area === "Finanças")[index]?.acao || (
                                                    <EmptyMessage> </EmptyMessage>
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {acoes.filter((a) => a.area === "Processos / Produção")[index]?.acao || (
                                                    <EmptyMessage> </EmptyMessage>
                                                )}
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>

            {/* Tabela invisível para exportação em PDF */}
            <Box 
                id="hidden-plano-acao-table" 
                sx={{ 
                    position: 'absolute', 
                    left: '-9999px', 
                    top: '-9999px',
                    width: '270mm', // Largura menor para melhor aproveitamento da página
                    backgroundColor: '#ffffff'
                }}
            >
                <Paper
                    elevation={2}
                    sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#ffffff !important',
                        border: '1px solid #e0e0e0',
                        boxShadow: 'none !important',
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: (theme) => theme.palette.primary.main,
                            color: 'white',
                            py: 2,
                            textAlign: 'center',
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                        }}
                    >
                        <Typography fontSize={20} fontWeight="semibold" component="h1">
                            Planos de Ação
                        </Typography>
                    </Box>

                    <TableContainer>
                        <Table
                            sx={{
                                tableLayout: "fixed",
                                width: "100%",
                                border: '1px solid #e0e0e0 !important',
                                '& td, & th': {
                                    border: '1px solid #e0e0e0 !important',
                                }
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell sx={{ width: '30%' }}>Ação</StyledTableCell>
                                    <StyledTableCell sx={{ width: '30%' }}>Meta Global</StyledTableCell>
                                    <StyledTableCell sx={{ width: '20%' }}>Responsável</StyledTableCell>
                                    <StyledTableCell sx={{ width: '20%' }}>Prazo</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {acoesDetalhadas.map((acao, index) => (
                                    <StyledTableRow key={acao.guid || index}>
                                        <StyledTableCell sx={{ textAlign: 'left', padding: '8px 12px' }}>
                                            {acao.area && acao.metaGlobalDescricao ? 
                                                `${acao.area}: ${acao.metaGlobalDescricao}` : 
                                                acao.metaGlobalDescricao || '-'}
                                        </StyledTableCell>
                                        <StyledTableCell sx={{ textAlign: 'left', padding: '8px 12px' }}>
                                            {acao.acao || '-'}
                                        </StyledTableCell>
                                        <StyledTableCell sx={{ textAlign: 'left', padding: '8px 12px' }}>
                                            {acao.responsavelNome || acao.responsavelGuid || '-'}
                                        </StyledTableCell>
                                        <StyledTableCell sx={{ textAlign: 'center', padding: '8px 12px' }}>
                                            {acao.prazoConclusao ? 
                                                new Date(acao.prazoConclusao).toLocaleDateString('pt-BR') : '-'}
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>

            <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                mt: 3
            }}>
                <PrimaryButton
                    variant="contained"
                    onClick={exportToPDF}
                    disabled={isExportingPDF || isLoadingMetas || isLoadingAcoes || isLoadingSwot}
                    sx={{
                        minWidth: '140px',
                        fontSize: '14px',
                        fontWeight: 600,
                    }}
                >
                    {isExportingPDF ? (
                        <>
                            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                            Exportando...
                        </>
                    ) : (
                        'Exportar PDF'
                    )}
                </PrimaryButton>
            </Box>
        </Box>
    )
}